import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import { useTranslationApp } from "../../lib/i18next";
import { SECTION_TYPOGRAPHY_ADD_FONT } from "../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { getReactHookFormNestedError } from "../../utils/errors";

const filterOptionsFn = createFilterOptions();

const MAX_MATCHES_TO_SHOW_ADD_OPTION = 5;

const hasOptionMatch = ({ options, inputValue }) =>
  options.some((option) => option.toLowerCase() === inputValue.toLowerCase());

const getValueFromOption = (newValue) => {
  if (!newValue) return "";
  if (typeof newValue === "string") return newValue.trim();
  if (newValue.inputValue) return newValue.inputValue.trim();
  return String(newValue).trim();
};

const CreatableAutocompleteField = ({
  control,
  name,
  label,
  placeholder = "",
  options = [],
  errors,
  addOptionLabelKey = SECTION_TYPOGRAPHY_ADD_FONT,
  required = false,
  onValueChange,
}) => {
  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(errors, name);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {label && (
        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
            fontSize: "14px",
          }}
        >
          {label}
        </Typography>
      )}
      <Controller
        control={control}
        name={name}
        rules={{ required: required ? true : false }}
        render={({ field }) => (
          <Autocomplete
            freeSolo
            options={options}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              if (option?.inputValue) return option.inputValue;
              return "";
            }}
            value={field.value || ""}
            onChange={(_, newValue) => {
              const nextValue = getValueFromOption(newValue);
              field.onChange(nextValue);
              onValueChange?.(nextValue);
            }}
            onBlur={(event) => {
              const blurValue = event.target.value?.trim() || "";
              field.onChange(blurValue);
              onValueChange?.(blurValue);
            }}
            filterOptions={(opts, params) => {
              const filtered = filterOptionsFn(opts, params);
              const inputValue = params.inputValue.trim();

              if (
                inputValue &&
                !hasOptionMatch({ options: opts, inputValue }) &&
                filtered.length < MAX_MATCHES_TO_SHOW_ADD_OPTION
              ) {
                filtered.unshift({
                  inputValue,
                  isAddOption: true,
                });
              }

              return filtered;
            }}
            renderOption={(props, option) => {
              if (option?.isAddOption) {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "primary.main",
                    }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2" component="span">
                      {t(addOptionLabelKey, {
                        value: option.inputValue,
                      })}
                    </Typography>
                  </Box>
                );
              }

              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  {option}
                </li>
              );
            }}
            isOptionEqualToValue={(option, val) => {
              if (!val) return false;
              if (typeof option === "string") {
                return option.toLowerCase() === val.toLowerCase();
              }
              return false;
            }}
            selectOnFocus
            handleHomeEndKeys
            fullWidth
            size="small"
            renderInput={(params) => (
              <MuiTextField
                {...params}
                variant="filled"
                placeholder={placeholder}
                error={!!error}
                helperText={error?.message}
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (event) => {
                    params.inputProps.onKeyDown?.(event);
                    if (!event.defaultPrevented && event.key === "Enter") {
                      const inputValue = event.target.value?.trim();
                      if (inputValue) {
                        field.onChange(inputValue);
                        onValueChange?.(inputValue);
                      }
                    }
                  },
                }}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  "& .MuiInputBase-root": {
                    paddingTop: "0px",
                    paddingBottom: "0px",
                    "&:before, &:after": {
                      display: "none",
                    },
                  },
                }}
              />
            )}
          />
        )}
      />
    </Box>
  );
};

export default CreatableAutocompleteField;
